import { type SxProps, type Theme } from '@mui/material';
import {
  covDialogBackdrop,
  covDialogPaper,
} from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';

const docsColumnRules: Array<{
  index: number;
  styles: SxProps<Theme>;
}> = [
  { index: 1, styles: { width: '18%' } },
  { index: 2, styles: { width: '9%' } },
  { index: 3, styles: { width: '12%', minWidth: { xs: 112, sm: 124, md: 136 } } },
  { index: 4, styles: { width: '12%' } },
  { index: 5, styles: { width: '9%', } },
  {
    index: 6,
    styles: {
      width: '7%',
      minWidth: { xs: 72, sm: 84, md: 92 },
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    },
  },
  {
    index: 7,
    styles: {
      width: '11%',
      minWidth: { xs: 116, sm: 124, md: 136 },
      whiteSpace: 'nowrap',
    },
  },
  {
    index: 8,
    styles: {
      width: '11%',
      minWidth: { xs: 104, sm: 124, md: 144 },
      whiteSpace: 'nowrap',
    },
  },
  {
    index: 9,
    styles: {
      width: '11%',
      minWidth: { xs: 104, sm: 124, md: 144 },
      whiteSpace: 'nowrap',
    },
  },
];

const docsColumnSelectorRules = docsColumnRules.reduce<Record<string, SxProps<Theme>>>(
  (accumulator, { index, styles }) => {
    accumulator[`& th:nth-of-type(${index}), & td:nth-of-type(${index})`] = styles;
    return accumulator;
  },
  {}
);

export const managerComponentStyles = {
  searchInput: (theme: Theme) => ({
    minHeight: { xs: 48, sm: 56 },
    backgroundColor: 'common.white',
    borderRadius: theme.spacing(1),
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cfd8e3',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#aebccc',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7f9bb8',
    },
  }),
  filterPaper: (theme: Theme) => ({
    p: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
    borderRadius: theme.spacing(1),
    borderColor: '#d7dee7',
    backgroundColor: 'common.white',
  }),
  filterIntro: (theme: Theme) => ({
    mb: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
  }),
  filterTitle: {
    fontWeight: 700,
    color: 'text.primary',
    fontSize: { xs: '0.95rem', sm: '1rem' },
  } as SxProps<Theme>,
  dateSpacer: {
    display: 'flex',
    justifyContent: { xs: 'stretch', md: 'flex-start' },
    alignItems: { xs: 'stretch', md: 'flex-end' },
    height: '100%',
    minHeight: { xs: 'auto', md: 72 },
  } as SxProps<Theme>,
  dateReportSpacer: (theme: Theme) => ({
    display: 'flex',
    justifyContent: { xs: 'stretch', md: 'flex-end' },
    alignItems: { xs: 'stretch', md: 'flex-end' },
    height: '100%',
    minHeight: { xs: 'auto', md: 72 },
    pr: { md: theme.spacing(0.5) },
  }),
  dateReportBtn: (theme: Theme) => ({
    minHeight: 40,
    width: { xs: '100%', sm: 'auto' },
    px: theme.spacing(3),
    borderRadius: theme.spacing(1),
    whiteSpace: 'nowrap',
  }),
  dateClearBtn: (theme: Theme) => ({
    minHeight: 40,
    width: { xs: '100%', sm: 'auto' },
    px: theme.spacing(3),
    borderRadius: theme.spacing(1),
    alignSelf: { xs: 'stretch', md: 'flex-end' },
    whiteSpace: 'nowrap',
  }),
  datePickerField: (theme: Theme) => ({
    '& .text-field-label': {
      mb: theme.spacing(1),
      pb: theme.spacing(0),
      color: 'text.secondary',
      fontSize: '0.875rem',
      lineHeight: 1.4375,
    },
    '& .custom-date-picker-input-wrapper': {
      position: 'relative',
    },
    '& .custom-date-picker': {
      minHeight: 40,
      padding: `${theme.spacing(1.0625)} ${theme.spacing(5.25)} ${theme.spacing(1.0625)} ${theme.spacing(1.75)}`,
      borderRadius: theme.spacing(1),
      borderColor: '#cfd8e3',
      backgroundColor: '#ffffff',
      fontSize: '0.875rem',
      letterSpacing: 'normal',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&::placeholder': {
        fontSize: '0.875rem',
        color: '#94a3b8',
        opacity: 1,
      },
      '&:hover': {
        borderColor: '#aebccc',
      },
      '&:focus-visible': {
        borderColor: '#7f9bb8',
        outline: 'none',
      },
    },
    '& .custom-date-picker-icon': {
      right: 12,
      width: 20,
      height: 20,
    },
    '& .custom-date-picker-icon-part': {
      stroke: '#64748b',
    },
    '& .text-field-error': {
      mt: theme.spacing(0.5),
    },
  }),
  sortableHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  }),
  sortableHeaderIcon: (isActive: boolean) => ({
    color: isActive ? 'primary.main' : 'text.secondary',
  }),
  tableActionBtn: (theme: Theme) => ({
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontWeight: 700,
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  }),
  rejectDialog: (theme: Theme) => ({
    '& .MuiBackdrop-root': covDialogBackdrop(theme),
    '& .MuiDialog-paper': covDialogPaper(theme),
  }),
  rejectTitle: {
    fontSize: '1rem',
    fontWeight: 700,
  } as SxProps<Theme>,
  rejectPrimary: (theme: Theme) => ({
    mb: theme.spacing(1),
  }),
  rejectSecondary: (theme: Theme) => ({
    mb: theme.spacing(2),
  }),
  docsRoot: (theme: Theme) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
    overflow: 'visible',
  }),
  docsTableContainer: (theme: Theme) => ({
    border: 'none',
    borderRadius: theme.spacing(0),
    backgroundColor: 'transparent',
    overflowX: { xs: 'auto', md: 'visible' },
    overflowY: 'visible',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  }),
  docsTableRoot: {
    width: '100%',
    maxWidth: '100%',
    tableLayout: 'fixed',
    minWidth: {
      xs: 900,
      sm: 980,
      md: 1080,
    },
    '& th, & td': {
      minWidth: 0,
    },
    ...docsColumnSelectorRules,
  } as SxProps<Theme>,
  docsTableHead: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
  }),
  docsTableHeaderRow: {
    borderBottom: '1px solid #dbe7f1',
  } as SxProps<Theme>,
  docsHeaderCell: (theme: Theme) => ({
    position: { xs: 'static', md: 'sticky' },
    top: { md: 0 },
    zIndex: { md: 4 },
    fontWeight: 800,
    color: theme.palette.common.black,
    textTransform: 'uppercase',
    fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
    backgroundColor: '#f5f9fc',
    borderBottom: '1px solid #dbe7f1',
    whiteSpace: 'normal',
    lineHeight: 1.15,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    px: { xs: theme.spacing(1), sm: theme.spacing(1.5), md: theme.spacing(2) },
    py: { xs: theme.spacing(0.875), sm: theme.spacing(1.125), md: theme.spacing(1.5) },
    '&.MuiTableCell-stickyHeader': {
      backgroundColor: '#f5f9fc',
    },
    '&:not(:last-of-type)': {
      borderRight: '1px solid #dbe7f1',
    },
  }),
  docsFileRow: {
    borderTop: '1px dashed rgba(148, 163, 184, 0.35)',
  } as SxProps<Theme>,
  docsBodyCell: (theme: Theme) => ({
    backgroundColor: '#ffffff',
    color: theme.palette.grey[700],
    fontSize: { xs: '0.9375rem', sm: '1rem', md: '1rem' },
    px: { xs: theme.spacing(1), sm: theme.spacing(1.5), md: theme.spacing(2) },
    py: { xs: theme.spacing(0.875), sm: theme.spacing(1.125), md: theme.spacing(1.5) },
    verticalAlign: 'middle',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  }),
  docsFileName: {
    color: 'grey.900',
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    minWidth: 0,
    flex: '1 1 auto',
  } as SxProps<Theme>,
  docsFileNameWrap: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    pl: { xs: theme.spacing(0.5), sm: theme.spacing(2) },
    minWidth: 0,
    flexWrap: 'wrap',
  }),
  docsViewedChip: (isViewed: boolean) => (theme: Theme) => ({
    flexShrink: 0,
    minWidth: 80,
    height: 24,
    borderRadius: '100px',
    border: 'none',
    color: isViewed ? theme.palette.primary.main : theme.palette.common.black,
    backgroundColor: `${isViewed ? theme.palette.primary['200'] : theme.palette.background.paper} !important`,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: `${isViewed ? '#C4E2FF' : '#dce0e6'} !important`,
    },
    '& .MuiChip-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: theme.spacing(1.125),
      py: theme.spacing(0.25),
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: isViewed ? 500 : 600,
      whiteSpace: 'nowrap',
    },
  }),
  docsGroupName: (theme: Theme) => ({
    color: theme.palette.common.black,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
  }),
  docsTermValue: (theme: Theme) => ({
    color: theme.palette.common.black,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
  }),
  docsSignerText: {
    fontSize: { xs: '0.9375rem', sm: '1rem' },
  } as SxProps<Theme>,
  sentChip: (theme: Theme) => ({
    '&.MuiChip-colorDefault': {
      color: theme.palette.common.black,
    },
  }),
  docsGroupRow: {
    backgroundColor: '#f8fafc',
    boxShadow: 'inset 4px 0 0 #dbe7f1',
    '& td, & th': {
      backgroundColor: '#f8fafc',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '&:hover td, &:hover th': {
      backgroundColor: '#f1f5f9',
    },
  } as SxProps<Theme>,
  docsComment: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 50,
  } as SxProps<Theme>,
  docsPreviewBtn: (theme: Theme) => ({
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontWeight: 700,
    borderRadius: '100px',
    boxShadow: 'none',
    minWidth: { xs: 40, sm: 90 },
    px: { xs: theme.spacing(0.75), sm: theme.spacing(1.5) },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: 0,
    },
    '&:hover': {
      boxShadow: 'none',
    },
  }),
  docsPreviewStack: (theme: Theme) => ({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
  }),
  decisionCheckbox: (isReadOnly: boolean) => ({
    color: isReadOnly ? 'action.disabled' : 'primary.main',
    opacity: isReadOnly ? 0.7 : 1,
    '&.Mui-checked': {
      color: isReadOnly ? 'action.disabled' : 'primary.main',
    },
    '&.Mui-disabled': {
      color: 'action.disabled',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 22,
    },
  }),
  assigneeMenu: {
    width: 320,
    maxWidth: '100%',
  } as SxProps<Theme>,
  assigneeSearch: (theme: Theme) => ({
    px: theme.spacing(1.5),
    pt: theme.spacing(1),
    pb: theme.spacing(1),
  }),
  assigneeSelected: (theme: Theme) => ({
    px: theme.spacing(1.5),
    pb: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  }),
  assigneeFilterActions: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    px: theme.spacing(1.5),
    pb: theme.spacing(1),
    pt: theme.spacing(0.5),
  }),
  statusFilterActions: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    px: theme.spacing(1.5),
    pb: theme.spacing(1),
    pt: theme.spacing(0.5),
  }),
  assigneeChip: (theme: Theme) => ({
    backgroundColor: `${theme.palette.common.white} !important`,
    borderRadius: theme.spacing(0.75),
    fontWeight: 500,
    height: 26,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      lineHeight: 1.1,
    },
    '&.MuiChip-outlined': {
      borderColor: theme.palette.primary.main,
    },
  }),
  readyDialogPaper: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    width: { xs: 'calc(100% - 24px)', md: '1080px' },
    maxWidth: { xs: 'calc(100% - 24px)', md: '1080px' },
    minHeight: { xs: '70vh', md: '76vh' },
    maxHeight: { xs: '85vh', md: '86vh' },
    display: 'flex',
    overflowX: 'hidden',
  }),
  readyDialogContent: (theme: Theme) => ({
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.palette.common.white,
  }),
  readyCard: {
    mb: 2,
    backgroundColor: '#fff',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      borderColor: 'primary.light',
      boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.2)',
    },
  } as SxProps<Theme>,
  readyListItemBtn: {
    borderRadius: 2,
    alignItems: 'flex-start',
    backgroundColor: '#fff',
    minHeight: 72,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
  } as SxProps<Theme>,
  readyTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  } as SxProps<Theme>,
  readyShortName: {
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  } as SxProps<Theme>,
  readyPreviewBox: (theme: Theme) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    px: theme.spacing(1.25),
    py: theme.spacing(1),
  }),
  readyPreviewGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
    gap: theme.spacing(1),
  }),
  readyPreviewItem: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  } as SxProps<Theme>,
  readyPreviewLabel: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontWeight: 600,
    lineHeight: 1.2,
  }),
  readyPreviewValue: {
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.35,
  } as SxProps<Theme>,
  readyPreviewMonitoringRow: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
  } as SxProps<Theme>,
  readyDetails: {
    p: 2,
    pt: 1,
  } as SxProps<Theme>,
  readyLongNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  } as SxProps<Theme>,
  readyInfoIcon: {
    width: 18,
    height: 18,
    color: 'text.secondary',
  } as SxProps<Theme>,
  readyLongName: {
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  } as SxProps<Theme>,
  readyCopyBtn: {
    minWidth: 32,
    width: 32,
    height: 32,
    borderRadius: 1,
  } as SxProps<Theme>,
  readyInlineStatus: {
    display: 'inline-flex',
    alignItems: 'center',
  } as SxProps<Theme>,
  readyTargetStatus: (color: 'success' | 'error') => (theme: Theme) => ({
    minWidth: 89,
    height: 27,
    borderRadius: '100px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    color: color === 'success' ? theme.palette.success.main : theme.palette.error.main,
    borderColor: color === 'success' ? theme.palette.success.main : theme.palette.error.main,
    backgroundColor: 'transparent',
    '& .MuiChip-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      px: theme.spacing(1.5),
      py: theme.spacing(0.5),
      lineHeight: '19px',
    },
  }),
  readyDocsLoadInitial: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    py: 1.5,
  } as SxProps<Theme>,
  readyNoDocsText: {
    color: 'text.secondary',
    py: 1,
  } as SxProps<Theme>,
  readyDocsErrorText: {
    color: 'error.main',
    py: 1,
  } as SxProps<Theme>,
  readyDocsTable: {
    maxHeight: 'none',
    overflow: 'visible',
  } as SxProps<Theme>,
  readyDocNameCell: {
    width: '58%',
  } as SxProps<Theme>,
  readyDocsLoadBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    py: 1.5,
  } as SxProps<Theme>,
} as const;
